import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  type?: 'website' | 'article';
  
  // AI Search Engine Optimization
  keywords?: string[];
  entities?: string[]; // Named entities for AI understanding
  topics?: string[]; // Topic modeling for AI
  
  // Article-specific
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  authorUrl?: string;
  section?: string;
  tags?: string[];
  
  // Rich media
  image?: string;
  imageAlt?: string;
  imageWidth?: number;
  imageHeight?: number;
  
  // Legacy props for backward compatibility
  ogType?: string;
  ogImage?: string;
  twitterCard?: string;
  twitterImage?: string;
  
  // Structured data
  structuredData?: Record<string, any> | Record<string, any>[];
  children?: React.ReactNode;
}

const SEO = ({
  title = 'CodeRide - End Context Amnesia in AI Coding',
  description = 'Stop re-explaining your project to AI agents. CodeRide eliminates context amnesia with persistent memory for vibe coders.',
  canonical = 'https://coderide.ai/',
  type = 'website',
  
  // AI Search Engine Optimization
  keywords,
  entities,
  topics,
  
  // Article-specific
  publishedTime,
  modifiedTime,
  author,
  authorUrl,
  section,
  tags,
  
  // Rich media
  image,
  imageAlt,
  imageWidth = 1200,
  imageHeight = 630,
  
  // Legacy props for backward compatibility
  ogType,
  ogImage,
  twitterCard = 'summary_large_image',
  twitterImage,
  
  // Structured data
  structuredData,
  children,
}: SEOProps) => {
  // Use new props or fall back to legacy props
  const finalType = type || ogType || 'website';
  const finalImage = image || ogImage || 'https://ideybnueizkxwqmjowpy.supabase.co/storage/v1/object/sign/coderide-website/Coderide-og-Facebook.jpg?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJjb2RlcmlkZS13ZWJzaXRlL0NvZGVyaWRlLW9nLUZhY2Vib29rLmpwZyIsImlhdCI6MTc0NTMxMzkyMiwiZXhwIjoxOTAyOTkzOTIyfQ.F4044p88xbiiA6SgvzixQ08uPtJa16_6yJFBx26n0Tc';
  const finalTwitterImage = twitterImage || finalImage || 'https://ideybnueizkxwqmjowpy.supabase.co/storage/v1/object/sign/coderide-website/Coderide-og-X.jpg?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJjb2RlcmlkZS13ZWJzaXRlL0NvZGVyaWRlLW9nLVguanBnIiwiaWF0IjoxNzQ1MzE0MTUzLCJleHAiOjE5MDI5OTQxNTN9.F_3s7MzrzJWx9Ut6gFSU6Pb4hx8PKwctJ3MzT6AGwb4';

  return (
    <Helmet>
      {/* Core SEO Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      <link rel="author" href="/humans.txt" />
      
      {/* AI Search Engine Optimization */}
      {keywords && keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(', ')} />
      )}
      {entities && entities.length > 0 && (
        <meta name="entities" content={entities.join(', ')} />
      )}
      {topics && topics.length > 0 && (
        <meta name="topics" content={topics.join(', ')} />
      )}
      
      {/* Enhanced Meta Tags for AI Understanding */}
      <meta name="content-type" content="text/html; charset=utf-8" />
      <meta name="language" content="en-US" />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      {modifiedTime && <meta httpEquiv="last-modified" content={modifiedTime} />}
      
      {/* Enhanced Open Graph for AI */}
      <meta property="og:type" content={finalType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:image:alt" content={imageAlt || title} />
      <meta property="og:image:width" content={imageWidth.toString()} />
      <meta property="og:image:height" content={imageHeight.toString()} />
      <meta property="og:site_name" content="CodeRide" />
      <meta property="og:locale" content="en_US" />

      {/* Article-specific Open Graph tags */}
      {finalType === 'article' && (
        <>
          {publishedTime && (
            <>
              <meta property="article:published_time" content={publishedTime} />
              <meta property="og:published_time" content={publishedTime} />
            </>
          )}
          {modifiedTime && (
            <>
              <meta property="article:modified_time" content={modifiedTime} />
              <meta property="og:updated_time" content={modifiedTime} />
            </>
          )}
          {author && (
            <meta property="article:author" content={author} />
          )}
          {section && (
            <meta property="article:section" content={section} />
          )}
          {tags && tags.map(tag => (
            <meta key={tag} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Enhanced Twitter Cards for AI */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:site" content="@coderide_ai" />
      <meta name="twitter:creator" content="@coderide_ai" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={finalTwitterImage} />
      <meta name="twitter:image:alt" content={imageAlt || title} />

      {/* Additional SEO Meta Tags */}
      <meta name="theme-color" content="#7867EE" />
      <meta name="msapplication-TileColor" content="#7867EE" />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(Array.isArray(structuredData) ? structuredData : [structuredData])}
        </script>
      )}

      {/* Additional Meta Tags */}
      {children}
    </Helmet>
  );
};

export default SEO;
